import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, ShoppingBag, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { waLink, generalMsg } from "@/lib/whatsapp";

const LINKS = [
  { label: "Inicio", to: "/" },
  { label: "Catálogo", to: "/tienda" },
];

export default function Header() {
  const { totals, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="bg-volt py-1.5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black sm:text-[11px]">
        10% OFF en tu 2da prenda — Envíos en Buenos Aires en 24h
      </div>
      <div className={`glass border-b transition-colors ${scrolled ? "border-white/10" : "border-transparent"}`}>
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="header-logo-button" aria-label="MM Store — Inicio">
            <Logo />
          </Link>

          <div className="hidden items-center gap-7 md:flex">
            {LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-display text-sm font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:text-volt"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <a
              href={waLink(generalMsg())}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-link"
              aria-label="WhatsApp"
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-300 transition-colors hover:border-wa hover:text-wa sm:flex"
            >
              <MessageCircle className="h-4.5 w-4.5" />
            </a>
            <button
              data-testid="nav-cart-trigger"
              onClick={() => setOpen(true)}
              aria-label="Abrir carrito"
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-volt hover:text-volt"
            >
              <ShoppingBag className="h-4.5 w-4.5" />
              {totals.units > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-volt px-1 font-mono text-[10px] font-bold text-black"
                >
                  {totals.units}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-trigger"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menú"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white md:hidden"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden border-t border-white/10 md:hidden"
            >
              <div className="flex flex-col gap-1 px-5 py-4">
                {LINKS.map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="py-2 font-display text-2xl font-black uppercase tracking-wide text-white transition-colors hover:text-volt"
                  >
                    {l.label}
                  </Link>
                ))}
                <a
                  href={waLink(generalMsg())}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-2 py-2 font-display text-2xl font-black uppercase tracking-wide text-wa"
                >
                  <MessageCircle className="h-5 w-5" /> WhatsApp
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
